import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MessageCircle, ArrowRight, Camera } from 'lucide-react';

const WA_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20would%20like%20to%20know%20more%20about%20Sri%20Aurobindo%20Montessori%20School';

// ─────────────────────────────────────────────────────────────────────────────
// Gallery data
// To add images: push { src: "/gallery/<slug>/filename.jpg", alt: "..." }
// objects into the images array for the relevant category.
// Pattern for alt text: "Child working with [material] at Montessori school
//   in West Marredpally, Secunderabad"
// ─────────────────────────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: 'environments',
    label: 'Environments',
    slug: 'environments',
    description: 'The prepared environment — open shelves, beautiful materials, natural light, and a deep sense of order.',
    images: [
      // { src: '/gallery/environments/classroom-shelf-01.jpg', alt: 'Montessori classroom shelf with materials at school in West Marredpally, Secunderabad' },
    ],
  },
  {
    id: 'children-at-work',
    label: 'Children at Work',
    slug: 'children-at-work',
    description: 'Children absorbed in purposeful work — sometimes for an uninterrupted half-hour on a single activity.',
    images: [
      // { src: '/gallery/children-at-work/golden-beads-01.jpg', alt: 'Child working with Montessori golden bead material at preschool in West Marredpally, Secunderabad' },
    ],
  },
  {
    id: 'community-mixed-ages',
    label: 'Community & Mixed Ages',
    slug: 'community-mixed-ages',
    description: 'Older children guiding younger ones, unprompted — the mixed-age dynamic that makes Montessori unique.',
    images: [
      // { src: '/gallery/community-mixed-ages/big-helps-small-01.jpg', alt: 'Older child helping younger child at Montessori school in Secunderabad' },
    ],
  },
  {
    id: 'celebrations-festivals',
    label: 'Celebrations & Festivals',
    slug: 'celebrations-festivals',
    description: 'Deepavali, Holi, Annual Day, birthdays — celebrations that bring the community together.',
    images: [
      // { src: '/gallery/celebrations-festivals/annual-day-01.jpg', alt: 'Annual Day celebration at Sri Aurobindo Montessori School in West Marredpally' },
    ],
  },
  {
    id: 'field-trips-activities',
    label: 'Field Trips & Activities',
    slug: 'field-trips-activities',
    description: 'Field trips, outdoor work, gardening, and activities that extend learning beyond the classroom walls.',
    images: [
      // { src: '/gallery/field-trips-activities/garden-work-01.jpg', alt: 'Children gardening during outdoor activity at Montessori school in Hyderabad' },
    ],
  },
];

const INTRO_BULLETS = [
  {
    bold: 'The furrowed brow of concentration —',
    text: 'a child completely absorbed, uninterrupted, sometimes for half an hour on a single work.',
  },
  {
    bold: 'Order and beauty —',
    text: 'every material in its designated place, on open child-height shelves, cared for by the children themselves.',
  },
  {
    bold: 'Small hands doing real things —',
    text: 'pouring, chopping, polishing, writing, gardening, washing up.',
  },
  {
    bold: 'Big helping small —',
    text: 'an older child guiding a younger one, unprompted.',
  },
  {
    bold: 'No uniforms, no fear, plenty of light —',
    text: 'children as themselves.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────

function EmptyCategory({ category }) {
  return (
    <div
      className="col-span-full flex flex-col items-center justify-center py-16 px-6 rounded-3xl border-2 border-dashed border-blue-200 bg-[#F8FAFF] text-center"
      data-testid={`empty-${category?.id ?? 'all'}`}
    >
      <div className="w-16 h-16 bg-[#EFF6FF] rounded-full flex items-center justify-center mb-5 border border-blue-100">
        <Camera size={26} className="text-[#93C5FD]" />
      </div>
      <p className="font-bold text-[#1E3A8A] text-base mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>
        {category ? `${category.label} — photos coming soon` : 'Photos coming soon'}
      </p>
      {category && (
        <p className="text-slate-400 text-sm max-w-sm leading-relaxed mt-1">{category.description}</p>
      )}
      <p className="text-slate-300 text-xs mt-4">Real school photographs will appear here shortly.</p>
    </div>
  );
}

function ImageCard({ src, alt, index }) {
  return (
    <div
      className="group relative overflow-hidden rounded-2xl bg-[#F4F7FB] aspect-[4/3] shadow-sm hover:shadow-[0_8px_30px_rgba(30,58,138,0.14)] transition-shadow duration-300"
      data-testid={`gallery-img-${index}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      {/* Alt-text overlay on hover — useful for accessibility context */}
      <div className="absolute inset-0 bg-[#1E3A8A]/0 group-hover:bg-[#1E3A8A]/50 transition-all duration-300 flex items-end p-4 opacity-0 group-hover:opacity-100">
        <p className="text-white text-xs leading-relaxed line-clamp-3">{alt}</p>
      </div>
    </div>
  );
}

export default function Gallery() {
  const [activeTab, setActiveTab] = useState('all');

  const activeCategory = activeTab === 'all' ? null : CATEGORIES.find(c => c.id === activeTab);

  const visibleImages = useMemo(() => {
    if (activeTab === 'all') {
      return CATEGORIES.flatMap(cat => cat.images.map(img => ({ ...img, categoryId: cat.id })));
    }
    return (activeCategory?.images ?? []).map(img => ({ ...img, categoryId: activeTab }));
  }, [activeTab, activeCategory]);

  const isEmpty = visibleImages.length === 0;

  return (
    <div className="pt-16">
      <Helmet>
        <title>Gallery | Life Inside Our Montessori Environments — Sri Aurobindo Montessori, Hyderabad</title>
        <meta
          name="description"
          content="Photos from inside an authentic Montessori school in West Marredpally, Secunderabad — children at work with materials, mixed-age moments, festivals, field trips and celebrations."
        />
      </Helmet>

      {/* ── BANNER ── */}
      <div className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Gallery</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="gallery-h1"
          >
            Look closely. The learning is in the details.
          </h1>
          <p className="text-blue-200 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            These aren't posed photographs of children in costumes holding props. This is an ordinary morning here — which is exactly what makes it extraordinary.
          </p>
        </div>
      </div>

      {/* ── INTRO — WHAT TO LOOK FOR ── */}
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-slate-500 text-base font-medium mb-6">As you browse, here's what to look for:</p>
          <ul className="space-y-4">
            {INTRO_BULLETS.map((item, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="w-6 h-6 bg-[#DBEAFE] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[#1E3A8A] font-black text-xs">
                  {i + 1}
                </span>
                <p className="text-slate-600 text-base leading-relaxed">
                  <span className="font-semibold text-[#1E3A8A]">{item.bold}</span>{' '}
                  {item.text}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── FILTER TABS + GRID ── */}
      <section className="py-14 bg-[#F4F7FB]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Filter tabs — scrollable on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-4 mb-8 scrollbar-none" data-testid="gallery-tabs">
            <button
              onClick={() => setActiveTab('all')}
              data-testid="tab-all"
              className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 ${
                activeTab === 'all'
                  ? 'bg-[#1E3A8A] text-white shadow-md'
                  : 'bg-white text-slate-500 border border-slate-200 hover:border-[#1E3A8A] hover:text-[#1E3A8A]'
              }`}
            >
              All
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                data-testid={`tab-${cat.id}`}
                className={`flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 ${
                  activeTab === cat.id
                    ? 'bg-[#1E3A8A] text-white shadow-md'
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-[#1E3A8A] hover:text-[#1E3A8A]'
                }`}
              >
                {cat.label}
                {cat.images.length > 0 && (
                  <span className="ml-2 bg-white/20 text-xs px-2 py-0.5 rounded-full">
                    {cat.images.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Category description (when a specific tab is active) */}
          {activeCategory && (
            <p className="text-slate-500 text-sm mb-6 max-w-2xl" data-testid="category-description">
              {activeCategory.description}
            </p>
          )}

          {/* Grid / Empty state */}
          <div className={isEmpty ? '' : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'}>
            {isEmpty ? (
              <EmptyCategory category={activeCategory} />
            ) : (
              visibleImages.map((img, i) => (
                <ImageCard key={`${img.src}-${i}`} src={img.src} alt={img.alt} index={i} />
              ))
            )}
          </div>

        </div>
      </section>

      {/* ── CLOSING CTA ── */}
      <section className="py-20 bg-[#1E3A8A]" data-testid="gallery-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Come visit</p>
          <h2
            className="text-3xl sm:text-4xl font-extrabold text-white mb-5"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Want to see it in real life?
          </h2>
          <p className="text-blue-200 text-base leading-relaxed mb-10 max-w-xl mx-auto">
            Photos capture the scene, but not the calm. Come feel the atmosphere yourself.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/admissions#enquiry-form"
              data-testid="gallery-cta-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              Book a Tour <ArrowRight size={16} />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
